import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { SiteShell } from "@/components/layout/SiteShell";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { TID } from "@/constants/testIds";
import { StatusBadge } from "@/pages/JobsList";

const APP_LABELS = {
  pending: { c: "bg-muted text-muted-foreground", label: "Pending" },
  // `text-[hsl(var(--sb-forest-2))]` on `bg-[hsl(var(--sb-moss))]` rendered as
  // deep-forest text on dark-moss in light mode — near-invisible. Switching
  // to cream text on the dark moss pill gives high contrast in both modes
  // (moss is dark in both themes so cream reads well either way).
  accepted: { c: "bg-[hsl(var(--sb-moss))] text-[hsl(var(--sb-cream))]", label: "Accepted" },
  declined: { c: "bg-destructive/10 text-destructive", label: "Declined" },
  withdrawn: { c: "bg-muted text-muted-foreground", label: "Withdrawn" },
};

export default function MyApplications() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/jobs/applications/mine/list"); setRows(data); }
      catch (e) { toast.error(formatApiError(e)); }
      finally { setLoading(false); }
    })();
  }, []);

  return (
    <SiteShell>
      <section className="container-page py-14" data-testid={TID.jobs.myApplicationsRoot}>
        <h1 className="font-serif text-4xl tracking-tight">Your applications</h1>
        <p className="mt-2 text-muted-foreground">Track what you've applied to and how it's going.</p>

        <div className="mt-10 space-y-4">
          {loading && Array.from({ length: 3 }).map((_, i) => <div key={i} className="card-soft h-32 animate-pulse" />)}
          {!loading && rows.length === 0 && (
            <div className="card-soft text-center py-16">
              <h3 className="font-serif text-2xl">No applications yet</h3>
              <p className="mt-2 text-sm text-muted-foreground">Browse open projects and send your first pitch.</p>
              <Link to="/jobs" className="btn-primary mt-6 inline-flex">Browse projects</Link>
            </div>
          )}
          {rows.map(({ application, job }) => (
            <div key={application.id} className="card-soft">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="flex items-center flex-wrap gap-2">
                    {job ? <StatusBadge status={job.status} /> : null}
                    <span className={`text-[11px] font-semibold uppercase tracking-[0.12em] rounded-full px-2.5 py-1 ${APP_LABELS[application.status]?.c || "bg-muted"}`}>
                      {APP_LABELS[application.status]?.label || application.status}
                    </span>
                  </div>
                  <h3 className="mt-3 font-serif text-2xl truncate">
                    {job ? <Link to={`/jobs/${job.id}`} className="link-underline">{job.title}</Link> : "Project removed"}
                  </h3>
                  <p className="mt-2 text-sm text-muted-foreground line-clamp-2">{application.cover_letter}</p>
                </div>
                <div className="text-sm text-muted-foreground text-right">
                  <div>{application.proposed_rate ? `$${application.proposed_rate}` : "—"}</div>
                  <div>{application.proposed_turnaround_days ? `${application.proposed_turnaround_days}d` : "—"}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>
    </SiteShell>
  );
}
